import styled from 'styled-components';

// Rail, canvas, and right dock share `surface`; only the dock/rail
// `outlineVariant` hairlines separate them.
export const CanvasViewport = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${({ theme: { colors = {} } }) =>
    colors.surface ?? '#0E0F18'};
  /* Two-tier dot grid: 24px fine texture + 120px anchor dots so the eye
     has reference points while panning. The handoff's polish-pass
     stylesheet collapsed this to a single 24px layer (L13261), but the
     authoring stylesheet (L2441-2447) keeps both with the rationale
     "larger anchor dots every 120px to give the eye reference points
     without competing with the nodes". Restoring the intent here. */
  background-image: ${({ theme }) =>
    theme.isLightTheme
      ? `radial-gradient(circle, rgba(80, 78, 200, 0.24) 1.6px, transparent 1.8px),
         radial-gradient(circle, rgba(80, 78, 200, 0.16) 1.2px, transparent 1.2px)`
      : `radial-gradient(circle, rgba(140, 142, 200, 0.20) 1.6px, transparent 1.8px),
         radial-gradient(circle, rgba(140, 142, 200, 0.12) 1.2px, transparent 1.2px)`};
  background-size:
    120px 120px,
    24px 24px;
  background-position: 0 0, 0 0;

  &[data-grid='off'] {
    background-image: none;
  }

  /* Our custom 'input' / 'output' node types collide with the library's
     built-in names; without these vars the default white rectangle
     leaks through behind NodeCardShell. */
  .react-flow {
    --xy-node-background-color-default: transparent;
    --xy-node-color-default: inherit;
    --xy-node-border-default: 0;
    --xy-node-border-radius-default: 0;
    --xy-node-boxshadow-hover-default: none;
    --xy-node-boxshadow-selected-default: none;
  }
  .react-flow__node {
    padding: 0;
    width: auto;
    text-align: left;
  }

  /* Every wrapper (PortHandles / OutputRow / BranchRow / ImageGenNodeBody)
     sets inset:0 + transparent background on its descendant .react-flow__handle,
     so a parent-level rule here is unreachable. Removed to avoid drift. */

  .react-flow__minimap {
    background: ${({ theme: { colors = {} } }) =>
      colors.surface ?? '#0E0F18'};
    border: 0;
    border-radius: 24px;
    box-shadow: ${({ theme }) =>
      theme.isLightTheme
        ? '0 4px 12px rgba(20, 20, 50, 0.10)'
        : '0 8px 22px rgba(0, 0, 0, 0.4)'};
    overflow: hidden;
  }

  .react-flow__controls {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainer ?? '#181927'};
    border: 1px solid
      ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
    border-radius: ${({ theme: { borderRadius = {} } }) =>
      borderRadius.small ?? '8px'};
    button {
      background: transparent;
      color: ${({ theme: { colors = {} } }) =>
        colors.onSurface ?? '#E6E6F2'};
      border-bottom: 1px solid
        ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
    }
    button:last-child {
      border-bottom: 0;
    }
  }

  .react-flow__selection,
  .react-flow__nodesselection-rect {
    background: ${({ theme: { colors = {} } }) =>
      colors.primary ? `${colors.primary}1f` : 'rgba(193, 193, 255, 0.12)'};
    border: 1px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
  }
`;

// `pointer-events: none` so the user can drop *through* the CTA onto
// the canvas behind it.
export const EmptyCanvasCta = styled.div`
  pointer-events: none;
  text-align: center;
  padding: 24px 28px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.large ?? '12px'};
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainer
      ? `${colors.surfaceContainer}cc`
      : 'rgba(31, 31, 39, 0.8)'};
  border: 1px dashed
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  max-width: 360px;
  backdrop-filter: blur(8px);
`;

export const EmptyCanvasCtaTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
`;

export const EmptyCanvasCtaSub = styled.div`
  font-size: 12.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  line-height: 1.5;
`;
